import React from 'react';
import clsx from 'clsx';
import useChat from '../hooks/useChat';
import useKnowledgeStamp from '../hooks/useKnowledgeStamp';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import PromptChips from './PromptChips';

/**
 * Layout-agnostic chat container. The same component composes inside both:
 *   - <ChatbotPage> (full-page at /chatbot)
 *   - <BubbleShell> (floating widget)
 *
 * The `layout` prop adjusts container styling only — all logic, all network,
 * all trust-surface UI is identical across layouts. Do not fork this.
 */

export default function ChatShell({ layout = 'page' }) {
  const { messages, send, isSending } = useChat();
  const isEmpty = messages.length === 0;
  const isPage = layout === 'page';

  return (
    <div
      data-drishti="chat-shell"
      data-layout={layout}
      className={clsx(
        'flex flex-col',
        isPage ? 'mx-auto min-h-screen max-w-3xl px-4 py-8 sm:px-6' : 'h-full w-full',
      )}
    >
      {isPage ? (
        <header className="pb-6">
          <h1 className="text-2xl font-semibold text-navy">Drishti</h1>
          <p className="text-sm text-slate-500">
            Ask about audit operations, CMPs, spans, fiber PM. Every answer is
            tagged and cites its source.
          </p>
        </header>
      ) : null}

      <main
        className={clsx(
          'flex-1 overflow-hidden rounded-2xl border border-app-border bg-surface shadow-sm',
          isPage ? 'p-4 sm:p-6' : 'p-3',
        )}
      >
        <div className="flex h-full min-h-[300px] flex-col gap-4">
          {isEmpty ? (
            <div className="flex flex-1 flex-col items-start justify-center gap-4">
              <div>
                <div className="text-[17px] font-semibold tracking-[-0.01em] text-navy">
                  Hi, I'm Drishti.
                </div>
                <div className="mt-1 text-[13px] leading-relaxed text-slate-500">
                  Ask about audit ops, CMPs, spans, fiber PM, or root causes.
                  Every answer is tagged and cites its source.
                </div>
              </div>
              <PromptChips onPick={send} disabled={isSending} />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              <MessageList messages={messages} />
            </div>
          )}

          <ChatInput onSend={send} disabled={isSending} />
        </div>
      </main>

      {isPage ? (
        <footer className="pt-4 text-xs text-slate-400">
          Knowledge last reviewed: <KnowledgeStamp />
        </footer>
      ) : null}
    </div>
  );
}

/**
 * Dynamic stamp: fetches /api/drishti/meta and renders the MD's frontmatter
 * values. Falls back to a placeholder if the endpoint is unreachable so the
 * rest of the UI still renders.
 */
function KnowledgeStamp() {
  const { version, last_reviewed, loading, errored } = useKnowledgeStamp();

  if (loading) return <span className="font-mono text-slate-300">…</span>;
  if (errored || !version) return <span className="font-mono">unavailable</span>;

  return (
    <span className="font-mono">
      {last_reviewed} (v{version})
    </span>
  );
}
